import { createFileRoute, Outlet, redirect, useMatchRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { GenericPageSkeleton } from '@/components/generic-page-skeleton';
import type { BreadcrumbEntry } from '@/components/page-breadcrumbs';
import { PageContent, PageHeader, PageHeaderLeftElement } from '@/components/page-header';
import { AppSidebar } from '@/components/sidebar';
import { navItems } from '@/components/sidebar/nav-items';
import { SimulationModeBanner } from '@/components/simulation-mode-banner';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { sessionQueryOptions } from '@/features/auth';

export const Route = createFileRoute('/_authenticated')({
	beforeLoad: async ({ context, location }) => {
		try {
			await context.queryClient.ensureQueryData(sessionQueryOptions);
		} catch {
			throw redirect({
				to: '/login',
				search: { redirect: location.href },
			});
		}
	},
	component: AuthenticatedLayout,
});

function useDefaultBreadcrumbs(): BreadcrumbEntry[] {
	const { t } = useTranslation();
	const matchRoute = useMatchRoute();

	const activeItem = navItems.find((item) => matchRoute({ to: item.to, fuzzy: true }));

	return activeItem ? [{ label: t(activeItem.labelKey) }] : [];
}

function AuthenticatedLayout() {
	const breadcrumbs = useDefaultBreadcrumbs();

	return (
		<SidebarProvider>
			<AppSidebar />
			<SidebarInset>
				<PageHeader>
					{/* In the sticky header, so the warning survives every route and scroll position. */}
					<SimulationModeBanner />
					<PageHeaderLeftElement breadcrumbs={breadcrumbs} />
				</PageHeader>
				<PageContent>
					<Suspense fallback={<GenericPageSkeleton />}>
						<Outlet />
					</Suspense>
				</PageContent>
			</SidebarInset>
		</SidebarProvider>
	);
}
